import BaseBranding from "%/dynamic-component-editor/classes/branding/base-branding";
import TextBrandingStyleOption
  from "%/dynamic-component-editor/options/text-branding-style-option";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useBrandingStore = (...args) => require("%/dynamic-component-editor/stores/branding-store").default(...args);

export default class TypographyBranding extends BaseBranding {
  static inheritanceStructure = {
    base: "standard",
    inherits: {
      accent: ["standard"],
      subtle: ["standard"],
    },
  };

  // Metadata consumed by the Studio Branding section. Lives on the
  // provider class so the sidebar / preview grid don't depend on a
  // separate constants file — `BaseBranding.allProviders` walks
  // these to build menu and preview specs.
  static categoryKey = "typography";
  static categoryTitle = "Typography";
  static categoryDescription = "Edit the heading, sub-heading, title, label, paragraph, and note styles that drive every typographic element across your destination.";

  // How the preview card renders this branding's sample. The
  // BrandingElementPreview reads this to pick between an iText
  // (text) and an iButton (button) preview shell.
  static previewKind = "text";
  static previewSampleText = "The quick brown fox";

  // The 6 element types this branding owns. Each (type, variant)
  // pair maps to one BrandingElement entity on the destination's
  // `themeElements` feed (18 entries total). Typography has no
  // size axis — `determineSize()` returns undefined and the seed
  // builder writes `brandedSize = ""`.
  static elementTypes = [
    { type: "BrandedHeadingElement", label: "Heading" },
    { type: "BrandedSubHeadingElement", label: "Sub Heading" },
    { type: "BrandedTitleElement", label: "Title" },
    { type: "BrandedLabelElement", label: "Label" },
    { type: "BrandedParagraphElement", label: "Paragraph" },
    { type: "BrandedNoteElement", label: "Note" },
  ];

  // Mirror of `inheritanceStructure` — base + every key in
  // `inherits`. Standard is the inheritance root; accent and subtle
  // walk back to it via `findNextInheritanceLevel`.
  static get variants() {
    return [
      this.inheritanceStructure.base,
      ...Object.keys(this.inheritanceStructure.inherits ?? {}),
    ];
  }

  static get sizes() {
    return [];
  }

  // Branding-config top-level key used by the legacy
  // `useBrandingStore().configuration` / `defaultTemplateConfiguration`
  // tables. For typography the config keys ARE the element types
  // (BrandedHeadingElement, BrandedSubHeadingElement, …) — there's
  // one config slice per element type, sliced by variant.
  static get brandingConfigKey() {
    return null;
  }

  static isThemableOptionKey(optionKey) {
    const keys = Object.keys(useBrandingStore().defaultTemplateConfiguration["BrandedHeadingElement"]["standard"]);
    return keys.includes(`default.${optionKey}`);
  }

  // Walk every (elementType, variant) coordinate, yielding a flat
  // list of preview specs the Branding section's primary content
  // hands to BrandingElementPreview. The `key` is unique per spec
  // so Vue can `:key` a v-for; `label` reads as
  // "Heading / Standard" etc.
  static getPreviewSpecs() {
    const specs = [];
    for (const { type, label } of this.elementTypes) {
      for (const variant of this.variants) {
        specs.push({
          key: `${type}|${variant}`,
          label: `${label} / ${this.titleCase(variant)}`,
          elementType: type,
          brandedVariant: variant,
          brandedSize: "",
          brandingClass: this,
        });
      }
    }
    return specs;
  }

  // One section per heading type (order + headings from `elementTypes`),
  // each holding that type's variant cards — clearer than one flat grid.
  static getPreviewGroups() {
    const specsByType = new Map();
    for (const spec of this.getPreviewSpecs()) {
      if (!specsByType.has(spec.elementType)) {
        specsByType.set(spec.elementType, []);
      }
      specsByType.get(spec.elementType).push(spec);
    }
    return this.elementTypes.map(({ type, label }) => ({
      heading: label,
      previews: specsByType.get(type) ?? [],
    }));
  }

  // Lookup helpers used by `BrandingElementPreview` to decide
  // whether to render an iText (typography) or iButton (button)
  // for this element type. Each branding provider owns the answer
  // for its own types.
  static ownsElementType(elementType) {
    return this.elementTypes.some(entry => entry.type === elementType);
  }

  // See `ButtonBranding.findNextInheritanceLevel` for the algorithm.
  // Typography keys `inherits` by variant alone (no size axis), and
  // values are bare variant names rather than `variant.size` strings,
  // so the parent variant is the LAST entry in the list. Coords with
  // no `inherits` entry are the base (`standard`) — return the base
  // and let the walker's same-coord guard stop the loop.
  // eslint-disable-next-line no-unused-vars
  findNextInheritanceLevel(variant, size) {
    const inheritanceLevels = TypographyBranding.inheritanceStructure.inherits[variant] || [];
    const nextVariant = inheritanceLevels.length > 0
      ? inheritanceLevels[inheritanceLevels.length - 1]
      : TypographyBranding.inheritanceStructure.base;
    return { variant: nextVariant, size: null };
  }

  determineType(elementConfig, breakpoint, state, availableBreakpoints) {
    const textStyleOption = this.processInheritance(
      elementConfig.options,
      "text-style-option",
      breakpoint,
      state,
      availableBreakpoints,
    );
    const textBrandingStyleOption = this.processInheritance(
      elementConfig.options,
      "text-branding-style-option",
      breakpoint,
      state,
      availableBreakpoints,
    );

    const types = Object.keys(TextBrandingStyleOption.VariantOptions);
    const typeFromElementType = types.find(type => {
      return TextBrandingStyleOption.VariantOptions[type] === elementConfig.type;
    });

    const chosenStyle = textStyleOption || textBrandingStyleOption;
    return TextBrandingStyleOption.VariantOptions[chosenStyle]
      ?? TextBrandingStyleOption.VariantOptions[typeFromElementType]
      ?? elementConfig.type;
  }

  determineVariant(elementConfig, breakpoint, state, availableBreakpoints) {
    const variant = this.processInheritance(elementConfig.options, "styled-text-variant", breakpoint, state, availableBreakpoints);
    return variant || "standard";
  }

  determineSize() {
    return undefined;
  }
}
